import { useState, useEffect, useRef, useCallback } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";

const LookupAutocomplete = ({
  label,
  value,
  onChange,
  onSearch,
  placeholder = "",
  disabled = false,
  required = false,
  error = false,
  helperText = "",
  getOptionLabel = (option) => option.name || "",
  isOptionEqualToValue = (option, value) => option.id === value.id,
}) => {
  const minSearchLength = 2;
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const debounceTimerRef = useRef(null);

  const fetchOptions = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.length < minSearchLength) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const results = await onSearch(searchTerm);
        if (Array.isArray(results)) {
          setOptions(results);
        } else {
          setOptions([]);
        }
      } catch (error) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [onSearch],
  );

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!inputValue || inputValue.length < minSearchLength) {
      setOptions([]);
      setLoading(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchOptions(inputValue);
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, fetchOptions]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
      }}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      options={options}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      noOptionsText={
        !inputValue || inputValue.length < minSearchLength
          ? "Start typing..."
          : "No options"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default LookupAutocomplete;
